<template>
  <v-container pa-0 class="record-announcement record-video" grid-list-md>
    <v-layout row justify-center>
      <v-flex xs12 justify-center align-content-start class="video-wrapper">
        <div
          class="record-video__video-holder"
          :style="{ 'padding-bottom': aspectRatio * 100 + '%' }"
        >
          <video
            key="current"
            v-if="isCurrentVideoVisible"
            :src="currentVideo.url"
            controls
            class="record-video__video video-expo"
            :class="{ flipped: currentVideo.flipped }"
          />
          <video
            v-show="isPreviewVideoVisible"
            key="preview"
            ref="preview"
            autoplay
            muted
            class="record-video__video"
          />
          <video
            key="recording"
            v-show="isRecordedVideoVisible"
            ref="recording"
            id="recording"
            :src="recordingSrc"
            controls
            class="record-video__video"
          />
          <svg-icon
            v-if="normalizedVideos.length === 0 && step === STEP.INITIAL"
            name="video"
            class="rtb-absolute-center rtb-color-primary"
          />
        </div>
        <v-icon
          class="record-icon"
          v-if="recorderState === RECORDER_STATE.RECORDING"
        >
          fiber_manual_record
        </v-icon>
      </v-flex>
    </v-layout>

    <v-layout
      row
      v-if="
        normalizedVideos.length > 0 &&
        step === STEP.INITIAL &&
        editing === false
      "
    >
      <v-flex d-flex align-center mb-2>
        <v-select
          v-model="currentVideoID"
          :items="normalizedVideos"
          item-value="id"
          item-text="title"
          dark
          hide-details
          dense
        />
      </v-flex>
      <v-flex shrink v-if="hasFeaturedRecording">
        <v-checkbox
          v-model="getFeaturedGameRecordingID"
          dark
          dense
          label="featured"
          hide-details
        />
      </v-flex>
      <v-flex shrink>
        <v-btn fab dark small @click="onEditTitle" aria-label="Edit title">
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn
          fab
          dark
          small
          @click="onPublish"
          aria-label="Publish Again"
          :disabled="publishing"
          :loading="publishing"
        >
          <v-icon>send</v-icon>
        </v-btn>
      </v-flex>
    </v-layout>

    <v-form
      v-if="editing && step === STEP.INITIAL"
      ref="editingForm"
      @submit.prevent="onEditFormSubmit"
    >
      <v-layout column mt-2>
        <v-flex>
          <v-text-field
            v-model="editTitle"
            label="Title"
            dark
            hide-details
            single-line
            dense
            :rules="titleRules"
          />
        </v-flex>
        <v-flex mt-3>
          <v-layout justify-center>
            <v-flex xs3>
              <v-btn type="submit" class="popup-button">Submit</v-btn>
            </v-flex>
            <v-flex xs3>
              <v-btn dark class="popup-button" @click="editing = false"
                >Cancel</v-btn
              >
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-form>

    <template v-if="!editing">
      <!-- Initial -->
      <v-layout row mt-1 v-if="step === STEP.INITIAL" justify-center>
        <v-flex d-flex xs3>
          <button type="button" @click="onRecordNewVideo" class="popup-button">
            Record new
          </button>
        </v-flex>
        <v-flex d-flex xs3>
          <button type="button" class="popup-button" @click="onUploadVideo">
            Upload
          </button>
        </v-flex>
        <v-flex d-flex xs3 v-if="hasVideos">
          <button type="button" class="popup-button" @click="onDeleteVideo">
            Delete
          </button>
        </v-flex>
        <v-flex d-flex xs3 v-if="hasVideos && isHost && isSuper">
          <button type="button" class="popup-button" @click="onDeleteVideos">
            Delete All
          </button>
        </v-flex>
      </v-layout>

      <!-- Prepare -->

      <v-layout mt-4 v-if="step === STEP.PREPARE" row justify-center>
        <v-flex xs4 d-flex>
          <button type="button" class="popup-button" @click="onStartRecording">
            Start Recording
          </button>
        </v-flex>
        <v-flex xs4 d-flex>
          <button
            type="button"
            class="popup-button"
            @click="onCancelRecordNewVideo"
          >
            Cancel
          </button>
        </v-flex>
      </v-layout>

      <!-- Record -->

      <v-layout mt-4 v-if="step === STEP.RECORD" row justify-center>
        <v-flex d-flex xs5>
          <button @click="onStopRecording" class="popup-button">
            Stop Recording
          </button>
        </v-flex>
      </v-layout>

      <!-- Submit -->

      <!-- TODO(Andrew): remove inline style -->
      <v-form
        ref="form"
        @submit.prevent="postAnnouncement"
        style="width: 100%"
        v-if="step === STEP.SUBMIT"
      >
        <v-layout mt-2 column>
          <v-flex>
            <v-text-field
              v-model="titleComputed"
              label="Title"
              dark
              :rules="titleRules"
            />
          </v-flex>
          <v-flex>
            <v-layout row justify-center>
              <v-flex xs4 d-flex>
                <button class="popup-button">SUBMIT</button>
              </v-flex>
              <v-flex xs4 d-flex>
                <button
                  type="button"
                  @click="onResetRecording"
                  class="popup-button"
                >
                  CANCEL
                </button>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-form>

      <!-- Upload -->

      <v-layout mt-4 v-if="step === STEP.UPLOAD" row justify-center>
        <v-flex xs5 d-flex>
          <button class="popup-button" @click="cancelUpload">
            <v-layout>
              <v-flex d-flex align-center>
                <v-progress-circular
                  indeterminate
                  size="18"
                  color="white"
                  class="spinner"
                />
              </v-flex>
              <v-flex d-flex align-center>Cancel Upload</v-flex>
            </v-layout>
          </button>
        </v-flex>
      </v-layout>
    </template>

    <input
      style="display: none !important"
      type="file"
      accept="video/*"
      ref="uploadInput"
      @change="onStartUploadVideo($event.target.files)"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { VForm } from "vuetify/lib"
import uniqid from "uniqid"

import { uploadMedia } from "@/services/storage.service"

import {
  TARGET_GAME,
  TARGET_CLIENT,
  MAX_RECORDING_TIME,
  ACTION_TYPES
} from "@/store/RecordingModule"
import { ActionTypes as TwilioModuleActionTypes } from "../../store/TwilioModule"

import { FormMixin } from "../../mixins/Form"

import { rules } from "../../constants/rules"

/**
 * @readonly
 * @enum {string}
 */
const STEP = {
  INITIAL: "INITIAL",
  PREPARE: "PREPARE",
  RECORD: "RECORD",
  SUBMIT: "SUBMIT",
  UPLOAD: "UPLOAD"
}

/**
 * @readonly
 * @enum {string}
 */
const RECORDER_STATE = {
  RECORDING: "RECORDING",
  STOPPED: "STOPPED"
}

import { Firebase } from "../../helpers"

/**
 * @param { { title?: string } } recording
 */
function titleNormalizer(recording) {
  return recording.title === undefined
    ? { ...recording, title: "Untitled" }
    : recording
}

export default {
  name: "RecordVideo",
  components: { VForm },
  mixins: [FormMixin],
  props: {
    clientID: {
      type: String,
      required: true
    },
    gameID: {
      type: String,
      required: false
    },
    videoTarget: {
      type: String,
      required: true
    },
    aspectRatio: {
      type: Number,
      default: 9 / 16
    },
    hasFeaturedRecording: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    // TODO: why 2 sources of thuth?
    publishing: false,
    recorderState: RECORDER_STATE.STOPPED,
    recorder: null,
    data: [],
    recordedBlob: null,
    uploadingFile: false,
    downloadUrl: null,
    localVideoTrack: null,
    localAudioTrack: null,
    uploadingVideo: false,
    fileUpload: null,
    step: STEP.INITIAL,
    lastVideoKey: null,
    recordingSrc: null,
    mediaStream: null,
    storedVideos: null,
    currentVideoID: null,
    editTitle: "",
    editing: false,
    title: "",
    STEP,
    RECORDER_STATE,
    titleRules: rules.announcementVideoTitle
  }),
  computed: {
    ...mapGetters("auth", ["user", "token", "isHost"]),
    ...mapGetters(["game"]),
    titleComputed: {
      get() {
        var d = new Date()
        var theDate =
          ("0" + d.getDate()).slice(-2) +
          "-" +
          ("0" + (d.getMonth() + 1)).slice(-2) +
          "-" +
          d.getFullYear() +
          " " +
          ("0" + d.getHours()).slice(-2) +
          ":" +
          ("0" + d.getMinutes()).slice(-2)
        return (
          this.title ||
          `${this.user.firstname} ${this.user.lastname} – ${theDate}`
        )
      },
      set(value) {
        this.title = value
      }
    },
    isSuper() {
      return this.user.super
    },
    normalizedVideos() {
      return this.storedVideos === null
        ? []
        : Firebase.normalizeSnapshotToArray(this.storedVideos).map(
            titleNormalizer
          )
    },
    hasVideos() {
      return this.normalizedVideos.length > 0
    },
    currentVideo() {
      const video = this.normalizedVideos.find(
        video => video.id === this.currentVideoID
      )
      if (video !== undefined) return video
      return null
    },
    isCurrentVideoVisible() {
      return this.currentVideo !== null && this.step === STEP.INITIAL
    },
    isRecording() {
      return this.recorderState === RECORDER_STATE.RECORDING
    },
    isPreviewVideoVisible() {
      return this.step === STEP.PREPARE || this.step === STEP.RECORD
    },
    isRecordedVideoVisible() {
      return this.step === STEP.SUBMIT || this.step === STEP.UPLOAD
    },
    getFeaturedGameRecordingID: {
      get() {
        return this.game?.featuredRecording?.recordingID === this.currentVideoID
      },
      async set() {
        console.log("SETTING!!!")
        await this.setFeaturedRecording({
          clientID: this.clientID,
          gameID: this.gameID,
          recordingID: this.currentVideoID
        })
      }
    }
  },
  async created() {
    if (!this.localVideoTrack && !this.localAudioTrack) {
      this.localVideoTrack = await this[
        TwilioModuleActionTypes.CREATE_LOCAL_VIDEO_TRACK
      ]()
      this.localAudioTrack = await this[
        TwilioModuleActionTypes.CREATE_LOCAL_AUDIO_TRACK
      ]()
    }
    this.mediaStream = new MediaStream([
      this.localVideoTrack.mediaStreamTrack,
      this.localAudioTrack.mediaStreamTrack
    ])
    this.$refs.preview.srcObject = this.mediaStream
    await this.getVideos()
  },
  beforeDestroy() {
    try {
      this.$refs.recording.pause()
      this.localVideoTrack.stop()
      this.localAudioTrack.stop()
    } catch (e) {
      console.warn(e.message)
    }
  },
  methods: {
    ...mapActions("twilio", [
      TwilioModuleActionTypes.CREATE_LOCAL_VIDEO_TRACK,
      TwilioModuleActionTypes.CREATE_LOCAL_AUDIO_TRACK
    ]),
    ...mapActions("recording", [
      "addAnnouncementVideo",
      "deleteAnnouncementVideo",
      "getAnnouncementVideos",
      "setFeaturedRecording"
    ]),
    async onPublish() {
      this.publishing = true
      await this.$store.dispatch(
        `recording/${ACTION_TYPES.RE_PUBLISH_RECORDING}`,
        {
          clientID: this.clientID,
          recordingID: this.currentVideoID
        }
      )
      this.publishing = false
    },
    async getVideos() {
      this.storedVideos = await this.getAnnouncementVideos({
        clientID: this.clientID,
        targetID: this.getTargetID()
      })

      if (this.storedVideos !== null) {
        if (this.normalizedVideos.length > 0) {
          this.currentVideoID = this.normalizedVideos[
            this.normalizedVideos.length - 1
          ].id
        }
      }
    },
    async onStopRecording() {
      this.recorderState = RECORDER_STATE.STOPPED
      this.recorder.state == "recording" && this.recorder.stop()
    },
    onStartRecording() {
      this.step = STEP.RECORD
      this.recorderState = RECORDER_STATE.RECORDING

      this.startRecording(this.mediaStream, MAX_RECORDING_TIME)
        .then(async recordedChunks => {
          this.recordedBlob = new Blob(recordedChunks, { type: "video/webm" })
          this.recordingSrc = URL.createObjectURL(this.recordedBlob)
          URL.revokeObjectURL(this.recordedBlob)

          this.recorderState = RECORDER_STATE.STOPPED
          this.step = STEP.SUBMIT
        })
        .catch(err => console.log(err.message))
    },
    onUploadVideo() {
      this.$refs.uploadInput.click()
    },
    onStartUploadVideo(fileList) {
      if (fileList.length) {
        let file = fileList.item(0)
        // Allow upload if file less then 150 MB (The Cloud Function limit)
        if (file.size / 1024 / 1024 > 150) {
          alert("Max allowed file size is 150MB")
          return
        }
        this.uploadVideo(file, true)
      } else {
        console.log("Have no selected video")
      }
    },
    wait(delayInMS) {
      return new Promise(resolve => setTimeout(resolve, delayInMS))
    },
    startRecording(stream, lengthInMS) {
      this.recorder = new MediaRecorder(stream)

      this.recorder.ondataavailable = event => this.data.push(event.data)
      this.recorder.start()

      let stopped = new Promise((resolve, reject) => {
        this.recorder.onstop = resolve
        this.data = []
        this.recorder.onerror = event => reject(event.name)
      })

      let recorded = this.wait(lengthInMS).then(() => {
        this.recorder.state === "recording" && this.recorder.stop()
      })

      return Promise.race([stopped, recorded]).then(() => this.data)
    },
    async uploadBlob(blob, name) {
      this.uploadingFile = true
      this.downloadUrl = null

      try {
        this.fileUpload = uploadMedia({
          fileName: `recorded-files/${name}`,
          blob,
          token: this.token
        })

        const url = await this.fileUpload
        this.downloadUrl = url
        return url
      } catch (e) {
        throw new Error(e.message)
      } finally {
        this.uploadingFile = false
      }
    },
    onRecordNewVideo() {
      this.step = STEP.PREPARE
    },
    onResetRecording() {
      this.recorder &&
        this.recorder.state === "recording" &&
        this.recorder.stop()
      this.recorderState = RECORDER_STATE.STOPPED
      this.recordingSrc = null
      this.data = []
      this.recordedBlob = null
      this.step = STEP.INITIAL
    },
    onCancelRecordNewVideo() {
      this.step = STEP.INITIAL
    },
    async postAnnouncement() {
      const valid = this.validateForm()
      if (valid) {
        this.uploadVideo(this.recordedBlob)
      }
    },
    async uploadVideo(blob, flipped = false) {
      this.uploadingVideo = true
      this.step = STEP.UPLOAD
      const fileName = `${uniqid()}-recorded-file-${this.user.id}.webm`

      try {
        const url = await this.uploadBlob(blob, fileName)
        this.uploadingVideo = false

        this.lastVideoKey = await this.addAnnouncementVideo({
          target: this.videoTarget,
          clientID: this.clientID,
          userID: this.user.id,
          name: fileName,
          targetID: this.getTargetID(),
          url,
          title: this.titleComputed,
          flipped
        })

        this.data = []
        this.title = ""
        this.$emit("onPosted")
      } catch (e) {
        console.error(e)
        this.step = STEP.INITIAL
      }
    },
    cancelUpload() {
      if (this.fileUpload.state === "running") {
        this.fileUpload.cancel()
      } else if (this.fileUpload.state === "success") {
        this.deleteAnnouncementVideo({
          recordingID: this.lastVideoKey,
          clientID: this.clientID
        })
      }

      this.uploadingVideo = false
      this.step = STEP.INITIAL
    },
    async onDeleteVideo() {
      if (confirm("Are you sure you want to delete this video")) {
        const { clientID } = this
        await this.$store.dispatch("recording/deleteAnnouncementVideo", {
          clientID,
          recordingID: this.currentVideoID
        })
        await this.getVideos()
      }
    },
    async onDeleteVideos() {
      if (confirm("Are you sure you want to delete all videos")) {
        const { clientID } = this
        await this.$store.dispatch("recording/deleteAnnouncementVideos", {
          clientID,
          targetID: this.getTargetID()
        })
        await this.getVideos()
      }
    },
    getTargetID() {
      if (this.videoTarget === TARGET_CLIENT) {
        return this.clientID
      } else if (this.videoTarget === TARGET_GAME) {
        return this.gameID
      }
    },
    onEditTitle() {
      this.editing = true
      this.editTitle = this.currentVideo.title || "Video"
    },
    async onEditFormSubmit() {
      const valid = this.$refs.editingForm.validate()

      if (valid) {
        await this.$store.dispatch(
          `recording/${ACTION_TYPES.UPDATE_RECORDING_TITLE}`,
          {
            clientID: this.clientID,
            recordingID: this.currentVideoID,
            title: this.editTitle
          }
        )
        this.currentVideo.title = this.editTitle
        this.editing = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.record-announcement {
  .video-wrapper {
    width: 100%;
    position: relative;
    .record-icon {
      position: absolute;
      right: 20px;
      top: 20px;
      border-radius: 50%;
      color: $color-orange;
      animation: pulse 2s infinite;
    }
  }
  .md4 {
    display: flex;
    flex-wrap: wrap;
  }
  .xs2:first-child .button {
    margin-left: 8px;
  }

  .popup-button {
    padding-left: 10px;
    padding-right: 10px;
    background-color: $primary_accent_color !important;
    color: #fff;
    // padding-top: 4px;
    // line-height: 20px;
  }

  // .spinner {
  //   margin-right: 5px;
  // }
}
@media (max-width: 767px) {
  .record-announcement {
    max-height: 90vh;
    overflow-x: hidden;
    overflow-y: auto;

    video {
      /*background: linear-gradient(to top, #010101 0%, #333 50%, #333);*/
      background: transparent;
      margin-bottom: 10px;
      max-width: 50vw;
      max-height: 80vh;
    }
  }
}
@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba($color-orange, 0.75);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

.record-video {
  &__video-holder {
    position: relative;
  }

  &__video {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
